/** React Query bindings for model catalog and per-feature preferences. */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getModelPreferences,
  listModels,
  saveModelPreferences,
} from "../api";
import type {
  AIModelListResponse,
  ByokProvider,
  ModelPreferencesResponse,
  SaveModelPreferencesRequest,
} from "../api";

export const modelPreferenceKeys = {
  all: ["model-choice"] as const,
  preferences: () => ["model-choice", "preferences"] as const,
  models: (provider: ByokProvider, feature?: string) =>
    ["model-choice", "models", provider, feature] as const,
};

export function useModels(provider: ByokProvider, feature?: string) {
  return useQuery<AIModelListResponse>({
    queryKey: modelPreferenceKeys.models(provider, feature),
    queryFn: () => listModels(provider, feature),
    staleTime: 15 * 60_000,
  });
}

/** Preload multiple provider catalogs without calling hooks in a loop. */
export function useModelCatalogs(
  providers: readonly ByokProvider[],
  feature?: string,
) {
  return useQueries({
    queries: providers.map((provider) => ({
      queryKey: modelPreferenceKeys.models(provider, feature),
      queryFn: () => listModels(provider, feature),
      staleTime: 15 * 60_000,
    })),
  });
}

export function useModelPreferences() {
  return useQuery<ModelPreferencesResponse>({
    queryKey: modelPreferenceKeys.preferences(),
    queryFn: getModelPreferences,
    staleTime: 30_000,
  });
}

export function useSaveModelPreferences() {
  const queryClient = useQueryClient();
  return useMutation<
    ModelPreferencesResponse,
    Error,
    SaveModelPreferencesRequest
  >({
    mutationKey: ["model-choice", "save"],
    mutationFn: saveModelPreferences,
    onSuccess: (response) => {
      queryClient.setQueryData(modelPreferenceKeys.preferences(), response);
      void queryClient.invalidateQueries({ queryKey: modelPreferenceKeys.all });
    },
  });
}
